/**
 * Arka plan bildirimleri:
 * 1. Oda beklerken (sudoku_active_room varken) yeni oyuncu / oyun başlangıcı
 * 2. Arkadaştan gelen challenge daveti (friend_challenge event)
 *
 * RoomPage'deyken bu component sessiz kalır (RoomPage kendi socket'ini yönetiyor).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { Modal } from './Modal';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface ActiveRoom {
  room_id: string;
  room_code: string;
  room_name: string;
}

export function getActiveRoom(): ActiveRoom | null {
  try {
    const raw = localStorage.getItem('sudoku_active_room');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getPlayerId(): string {
  let id = localStorage.getItem('sudoku_player_id');
  if (!id) {
    id = 'p_' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sudoku_player_id', id);
  }
  return id;
}

export function RoomNotifier() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { t } = useI18n();
  const rn = t.roomNotifier as any;

  // ── Oda bildirimi (active room) ───────────────
  const roomSocketRef = useRef<Socket | null>(null);
  const [notice, setNotice] = useState<{ title: string; message: string; roomId?: string } | null>(null);
  const [activeRoom, setActiveRoom] = useState<ActiveRoom | null>(getActiveRoom);
  const knownPlayersRef = useRef<Set<string>>(new Set());

  // ── Arkadaş challenge bildirimi ───────────────
  const authSocketRef = useRef<Socket | null>(null);
  const [challengeNotice, setChallengeNotice] = useState<{
    room_id: string;
    room_code: string;
    challenger_nickname: string;
    difficulty: number;
  } | null>(null);

  // localStorage'daki aktif odayı yol değişikliklerinde yeniden oku
  useEffect(() => {
    setActiveRoom(getActiveRoom());
  }, [location.pathname]);

  const isOnRoomPage = location.pathname.startsWith('/room/');

  // ── Oda izleme socket ────────────────────────
  const activeRoomId = activeRoom?.room_id ?? null;

  useEffect(() => {
    if (!activeRoomId || isOnRoomPage) {
      if (roomSocketRef.current) {
        roomSocketRef.current.disconnect();
        roomSocketRef.current = null;
      }
      knownPlayersRef.current.clear();
      return;
    }

    if (roomSocketRef.current?.connected) return;

    roomSocketRef.current?.disconnect();

    const roomId = activeRoomId;
    const roomName = activeRoom?.room_name ?? '';
    const playerId = getPlayerId();
    // Bu bağlantı için son bilinen oda durumu — room_watching snapshot'ı ve
    // game_started/room_finished event'lerinin AYNI geçiş için çift bildirim
    // göstermesini engeller (ör. watcher reconnect olurken snapshot zaten
    // "playing" görür, hemen arkasından gerçek game_started da gelirse).
    let lastKnownStatus: 'waiting' | 'playing' | 'finished' | null = null;

    if (!token) return;
    const s = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    roomSocketRef.current = s;

    s.on('connect', () => {
      // Salt-okunur izleme — join_room DEĞİL: bu socket odayı gerçekten
      // oynamıyor, sadece bildirim için dinliyor. join_room kullanmak,
      // oyuncu odadan çıkıp uygulamada gezinirken bile "reconnect oldu"
      // sanılmasına ve rage-quit'in hiç tetiklenmemesine yol açıyordu.
      s.emit('watch_room', { room_id: roomId, player_id: playerId });
    });

    s.on('room_watching', (data: any) => {
      const players = data.players || {};
      knownPlayersRef.current = new Set(Object.keys(players));
      if (data.room_code) {
        localStorage.setItem('sudoku_active_room', JSON.stringify({
          room_id: roomId,
          room_code: data.room_code,
          room_name: data.room_name || roomName,
        }));
      }

      // Reconnect senaryosu: bu izleyici socket'i bağlanmadan ÖNCE oyun zaten
      // bitmiş/başlamış olabilir (game_started/room_finished event'i kaçmış
      // olur — watcher o an bağlı değildi). Snapshot'taki status bunu telafi
      // eder. lastKnownStatus aynı geçişin biraz sonra gelecek gerçek
      // event'ten (game_started/room_finished) TEKRAR bildirim üretmesini
      // engeller.
      if (data.status === 'finished' && lastKnownStatus !== 'finished') {
        lastKnownStatus = 'finished';
        localStorage.removeItem('sudoku_active_room');
        setActiveRoom(null);
        setNotice({ title: rn.gameOverTitle, message: rn.gameOverMsg, roomId });
      } else if (data.status === 'playing' && lastKnownStatus !== 'playing') {
        lastKnownStatus = 'playing';
        setNotice({ title: rn.gameStartedTitle, message: rn.gameStartedMsg, roomId });
      } else {
        lastKnownStatus = data.status ?? lastKnownStatus;
      }
    });

    s.on('player_joined', (data: any) => {
      const players = data.players || {};
      const ids = Object.keys(players);
      const maxPlayers: number = data.max_players || 0;

      const newcomers = ids.filter(id => !knownPlayersRef.current.has(id) && id !== playerId);
      if (newcomers.length > 0) {
        const nick = players[newcomers[0]]?.nickname || rn.defaultPlayerJoined;
        setNotice({
          title: rn.newPlayerTitle,
          message: rn.newPlayerMsg.replace('{nick}', nick),
          roomId,
        });
      }

      knownPlayersRef.current = new Set(ids);

      if (newcomers.length > 0 && maxPlayers > 0 && ids.length >= maxPlayers) {
        setNotice({
          title: rn.roomFullTitle,
          message: rn.roomFullMsg,
          roomId,
        });
      }
    });

    s.on('game_started', () => {
      if (lastKnownStatus === 'playing') return; // room_watching snapshot'ı zaten bildirdi
      lastKnownStatus = 'playing';
      setNotice({
        title: rn.gameStartedTitle,
        message: rn.gameStartedMsg,
        roomId,
      });
    });

    s.on('room_finished', () => {
      if (lastKnownStatus === 'finished') return; // room_watching snapshot'ı zaten bildirdi
      lastKnownStatus = 'finished';
      // Oyun bitti — artık "bekleyen" bir oda değil, kalıcı aktif-oda
      // işaretini temizle. Sonuç ekranına dönebilme roomId üzerinden hâlâ
      // mümkün (notice kendi içinde taşıyor), activeRoom'a bağımlı değil.
      localStorage.removeItem('sudoku_active_room');
      setActiveRoom(null);
      setNotice({
        title: rn.gameOverTitle,
        message: rn.gameOverMsg,
        roomId,
      });
    });

    s.on('room_gone', () => {
      // Üyelik başka bir yerden (explicit leave) sona erdi — izlenecek
      // bir şey kalmadı, bildirim göstermeye gerek yok.
      localStorage.removeItem('sudoku_active_room');
      setActiveRoom(null);
      setNotice(null);
    });

    s.on('error', () => {
      localStorage.removeItem('sudoku_active_room');
      setActiveRoom(null);
    });

    return () => {
      s.disconnect();
      roomSocketRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId, isOnRoomPage, token]);

  // ── Arkadaş challenge socket (giriş yapılmışsa her zaman aktif) ──
  const userId = user?.id ?? null;

  // ── Presence socket (her ziyaretçi sayılsın diye her zaman bağlı) ──
  const presenceSocketRef = useRef<Socket | null>(null);
  useEffect(() => {
    if (isOnRoomPage || !token) {
      presenceSocketRef.current?.disconnect();
      presenceSocketRef.current = null;
      return;
    }
    if (presenceSocketRef.current?.connected) return;
    const s = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    presenceSocketRef.current = s;
    return () => {
      s.disconnect();
      presenceSocketRef.current = null;
    };
  }, [isOnRoomPage, token]);

  useEffect(() => {
    if (!userId || isOnRoomPage) {
      authSocketRef.current?.disconnect();
      authSocketRef.current = null;
      return;
    }

    if (authSocketRef.current?.connected) return;

    authSocketRef.current?.disconnect();

    if (!token) return;
    const s = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    authSocketRef.current = s;

    s.on('connect', () => {
      s.emit('authenticate', { user_id: userId });
    });

    s.on('friend_challenge', (data: {
      room_id: string;
      room_code: string;
      challenger_nickname: string;
      difficulty: number;
    }) => {
      setChallengeNotice(data);
    });

    return () => {
      s.disconnect();
      authSocketRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isOnRoomPage, token]);

  // ── Oda bildirimi handlers ─────────────────────
  const handleRoomConfirm = () => {
    const roomId = notice?.roomId ?? activeRoom?.room_id;
    setNotice(null);
    if (roomId) navigate(`/room/${roomId}`);
  };

  const handleRoomCancel = () => setNotice(null);

  // ── Challenge handlers ─────────────────────────
  const handleChallengeAccept = () => {
    if (!challengeNotice) return;
    const playerId = getPlayerId();
    const nickname = localStorage.getItem('sudoku_nickname') || user?.nickname || rn.defaultPlayer;
    localStorage.setItem('sudoku_active_room', JSON.stringify({
      room_id: challengeNotice.room_id,
      room_code: challengeNotice.room_code,
      nickname,
      player_id: playerId,
    }));
    setChallengeNotice(null);
    navigate(`/room/${challengeNotice.room_id}`);
  };

  const handleChallengeDecline = () => setChallengeNotice(null);

  return (
    <>
      {notice && (
        <Modal
          open={true}
          title={notice.title}
          message={notice.message}
          confirmText={rn.backToRoom}
          cancelText={rn.later}
          onConfirm={handleRoomConfirm}
          onCancel={handleRoomCancel}
        />
      )}
      {challengeNotice && (
        <Modal
          open={true}
          title={rn.challengeTitle}
          message={rn.challengeMsg.replace('{nick}', challengeNotice.challenger_nickname)}
          confirmText={rn.accept}
          cancelText={rn.decline}
          onConfirm={handleChallengeAccept}
          onCancel={handleChallengeDecline}
        />
      )}
    </>
  );
}
